// Plan de Emergencias v2 (bloques PE-A3a y PE-A3a2): capa de datos del asistente, pasos 1 y 2.
// Mismo patrón que psicosocial-persistencia.ts: cada función desestructura { data, error } y, si hay
// error, lanza un Error con mensaje en español.
//
// Fuente de los tipos: supabase/migrations/20261026070000_plan_emergencias_v2_modelo.sql (M33, aplicada
// por JC el 26/10/2026) y 20261026080000_plan_emergencias_v2_columnas.sql (M34, aplicada por JC el 27/10/2026).
// Las columnas base de emergency_plans (creadas a mano antes de M33, CONTEXTO §23) salen del tipo de
// EmergencyPlanPage.tsx. pe-datos.test.ts compara cada tipo con las migraciones.
//
// Reglas:
//  - Los totales (Σ, Qs, nivel, clasificación) NUNCA se guardan en sectores ni en filas: se calculan con
//    src/lib/pe al mostrar y se guardan solo en emergency_assessments, con sus trazas.
//  - RLS (M32/M33): ver = empresa; crear y editar = admin y manager; borrar = admin; superadmin todo.
//    Un INSERT bloqueado da 42501; un UPDATE o DELETE bloqueado no da error, solo 0 filas: por eso se
//    pide .select() y 0 filas también se informa como falta de permiso.
//
// M34: zona sísmica, bomberos y cantón INEC van en emergency_plans; el R del sector, en
// emergency_plan_sectors. Antes (PE-A3a) iban en emergency_assessments.inputs; M34 los copia. Las trazas
// siguen en emergency_assessments. Defensa que se conserva: si una base no tiene M34 (otro entorno, una
// restauración) o PostgREST aún no recargó su caché de esquema, guardar da 42703 / PGRST204 y mensajeErrorPE
// lo explica en vez de mostrar el error crudo.

import { supabase } from '../../supabaseClient';
import type { ZonaSismica } from './pe-amenazas';
import type { OrigenCoeficiente } from './pe-carga-fuego';
import { CI_NOTA_COMPACTO, materialPorClave, permiteCiCompacto } from './pe-materiales-rd164';
import type { AccionEditable, FilaAccion } from './pe-acciones';
import { ITEM_PO18, validarAccion } from './pe-acciones';
import type { Traza } from './pe-tipos';
import type { Continuidad, Transferencia } from './pe-continuidad';
import { continuidadVaciaDel, limpiarContinuidad, validarContinuidad, validarTransferencia } from './pe-continuidad';

export const PE_MOTOR_VERSION = 'pe-a3a2-2026-10-26';
export const PE_TABLAS_VERSION = 'rd164-2025:t133+t135-verificadas-boe; t132/t134-transcritas; am1257-art139; nec-se-ds-t19; inec-cge-2026';

// ── Listas de los CHECK de M33 ───────────────────────────────────────────────

export const OCUPACIONES = [
  'industrial', 'almacenamiento', 'oficinas', 'mercantil', 'residencial', 'alojamiento', 'educacion', 'salud', 'reunion_publica', 'otra',
] as const;
export type Ocupacion = typeof OCUPACIONES[number];

export const METODOS_FILA = ['materiales', 'fabricacion', 'almacenamiento'] as const;
export type MetodoFila = typeof METODOS_FILA[number];
export const Q_UNIDADES = ['MJ/kg', 'MJ/m2', 'MJ/m3'] as const;
export type QUnidad = typeof Q_UNIDADES[number];
export const Q_FUENTES = ['rd164_t133', 'rd164_t135', 'manual'] as const;
export type QFuente = typeof Q_FUENTES[number];
export const CI_ORIGENES = ['tabla', 'manual'] as const;
// Listas de los CHECK de M34
export const ZONAS_SISMICAS = ['I', 'II', 'III', 'IV', 'V', 'VI'] as const;
export const FUENTES_ZONA = ['nec_tabla19', 'manual'] as const;
export type FuenteZona = typeof FUENTES_ZONA[number];
export const R_ORIGENES = ['tabla', 'manual'] as const;
export const R_TABLA = [0.8, 1, 1.4, 1.8];
export const METODOS_EVALUACION = [
  'carga_fuego', 'meseri', 'verificacion', 'aforo_egreso', 'rset', 'punto_encuentro', 'amenaza_vulnerabilidad',
] as const;
export type MetodoEvaluacion = typeof METODOS_EVALUACION[number];

// ── Filas (snake_case, columna por columna) ──────────────────────────────────

export interface FilaPlan {
  // base (antes de M33)
  id: string;
  company_id: string;
  threats: string | null;
  procedures: string | null;
  resource_map_notes: string | null;
  evacuation_map_notes: string | null;
  maintenance_schedule: string | null;
  status: 'borrador' | 'vigente';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // M33 §1
  code: string | null;
  current_version: number | null;
  sede_id: string | null;     // OBSOLETA desde M40 (siempre null): la sede del plan es site_id
  site_canton: string | null;
  site_province: string | null;
  site_address: string | null;
  site_occupancy: Ocupacion | null;
  site_area_m2: number | null;
  site_floors: number | null;
  site_height_m: number | null;
  site_workers: number | null;
  site_shift: string | null;
  site_is_new: boolean | null;
  premovement_s: number | null;
  assembly_m2_per_person: number | null;
  assembly_min_distance_m: number | null;
  responsible_worker_id: string | null;
  // M34
  seismic_zone: ZonaSismica | null;
  seismic_zone_source: FuenteZona | null;
  seismic_place: string | null;
  fire_station_km: number | null;
  fire_station_min: number | null;
  canton_code: string | null;
  // M40 (F1a): sede del plan (la Matriz si no se eligió otra). Opcional: sin M40 en la base no llega.
  site_id?: string | null;
  // M37 (PE-A4): continuidad del negocio (jsonb, pe-continuidad.ts) y transferencia de riesgos. Opcionales: sin
  // M37 en la base no llegan.
  business_continuity?: unknown | null;
  risk_transfer?: boolean | null;
  risk_transfer_type?: string | null;
  risk_transfer_insurer?: string | null;
  risk_transfer_valid_until?: string | null;
}

export interface FilaSector {
  id: string;
  company_id: string;
  plan_id: string;
  name: string;
  sort_order: number;
  occupancy: Ocupacion;
  area_m2: number;
  max_storage_height_m: number | null;
  fire_separated: boolean;
  group_key: string | null;
  occupants: number | null;
  has_sprinklers: boolean;
  warehouse_id: string | null;
  zone_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // M34
  r_value: number | null;
  r_origin: OrigenCoeficiente | null;
  r_source: string | null;
}

export interface FilaCargaFuego {
  id: string;
  company_id: string;
  sector_id: string;
  sort_order: number;
  method: MetodoFila;
  material: string;
  table_ref: string | null;
  q_value: number;
  q_unit: QUnidad;
  q_source: QFuente;
  q_source_note: string | null;
  mass_kg: number | null;
  surface_m2: number | null;
  height_m: number | null;
  ci: number;
  ci_origin: OrigenCoeficiente;
  ci_source: string | null;
  ci_grade: number | null;
  ci_smoke: boolean;
  product_id: string | null;
  created_at: string;
  // M37 (PE-A4): de dónde salió la fila, p. ej. "Propuesto desde el inventario (bodega X)".
  origin_note?: string | null;
}

export interface FilaEvaluacion {
  id: string;
  company_id: string;
  plan_id: string;
  sector_id: string | null;
  method: MetodoEvaluacion;
  inputs: Record<string, unknown>;
  result: Record<string, unknown>;
  traces: Traza[];
  engine_version: string;
  tables_version: string | null;
  computed_at: string;
  computed_by: string | null;
}

// numeric llega como number o como texto según el cliente: se normaliza.
const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

export function mapPlan(r: any): FilaPlan {
  if (!r?.id || !r?.company_id) throw new Error('Fila de emergency_plans incompleta');
  return {
    ...r,
    current_version: num(r.current_version), site_area_m2: num(r.site_area_m2), site_floors: num(r.site_floors),
    site_height_m: num(r.site_height_m), site_workers: num(r.site_workers), premovement_s: num(r.premovement_s),
    assembly_m2_per_person: num(r.assembly_m2_per_person), assembly_min_distance_m: num(r.assembly_min_distance_m),
    seismic_zone: r.seismic_zone ?? null, seismic_zone_source: r.seismic_zone_source ?? null, seismic_place: r.seismic_place ?? null,
    fire_station_km: num(r.fire_station_km), fire_station_min: num(r.fire_station_min), canton_code: r.canton_code ?? null,
  };
}

export function mapSector(r: any): FilaSector {
  if (!r?.id || !r?.plan_id) throw new Error('Fila de emergency_plan_sectors incompleta');
  return {
    ...r, area_m2: Number(r.area_m2), max_storage_height_m: num(r.max_storage_height_m), occupants: num(r.occupants),
    sort_order: Number(r.sort_order ?? 0), r_value: num(r.r_value), r_origin: r.r_origin ?? null, r_source: r.r_source ?? null,
  };
}

export function mapFilaCarga(r: any): FilaCargaFuego {
  if (!r?.id || !r?.sector_id) throw new Error('Fila de emergency_fire_load_items incompleta');
  return {
    ...r, origin_note: r.origin_note ?? null, q_value: Number(r.q_value), ci: Number(r.ci), mass_kg: num(r.mass_kg), surface_m2: num(r.surface_m2),
    height_m: num(r.height_m), ci_grade: num(r.ci_grade), sort_order: Number(r.sort_order ?? 0),
  };
}

// ── Errores ──────────────────────────────────────────────────────────────────

export const MENSAJE_SIN_PERMISO =
  'No tienes permiso para este cambio: en el Plan de Emergencias solo el administrador y el gestor guardan, y solo el administrador borra.';

export function mensajeErrorPE(error: { code?: string; message?: string } | null | undefined, accion: string): string {
  if (!error) return `No se pudo ${accion}.`;
  if (error.code === '42501') return MENSAJE_SIN_PERMISO;
  if (error.code === '23514') return `No se pudo ${accion}: un dato no cumple las reglas del plan (${error.message ?? 'restricción'}).`;
  if (error.code === '23505') return `No se pudo ${accion}: ya existe un registro igual.`;
  if (error.code === '23503') return `No se pudo ${accion}: el registro relacionado ya no existe.`;
  if (error.code === '42703' || error.code === 'PGRST204') {
    return `No se pudo ${accion}: falta actualizar la base de datos del Plan de Emergencias v2. Avisa al soporte de StorSafe.`; // M34, M35 o M37
  }
  return `No se pudo ${accion}: ${error.message ?? 'error desconocido'}.`;
}

function falla(error: any, accion: string): never {
  throw new Error(mensajeErrorPE(error, accion));
}

// UPDATE/DELETE bloqueados por RLS devuelven 0 filas sin error.
function exigeFilas<T>(data: T[] | null, accion: string): T[] {
  if (!data || data.length === 0) throw new Error(`${MENSAJE_SIN_PERMISO} (no se pudo ${accion})`);
  return data;
}

// ── Plan (paso 1) ────────────────────────────────────────────────────────────

export async function cargarPlan(companyId: string): Promise<FilaPlan | null> {
  const { data, error } = await supabase.from('emergency_plans').select('*').eq('company_id', companyId).maybeSingle();
  if (error) falla(error, 'leer el plan');
  return data ? mapPlan(data) : null;
}

export interface DatosGenerales {
  code: string | null;
  canton_code: string | null;
  site_canton: string | null;
  site_province: string | null;
  site_area_m2: number | null;
  site_occupancy: Ocupacion | null;
  site_workers: number | null;
  seismic_zone: ZonaSismica | null;
  seismic_zone_source: FuenteZona | null;
  seismic_place: string | null;    // población de la Tabla 19 (obligatoria si la fuente es nec_tabla19)
  fire_station_km: number | null;
  fire_station_min: number | null;
  // Edificación nueva (true) o existente (false): decide la Tabla 3 o 4 de la RTQ 5 (PE-36). Columna de M33.
  site_is_new?: boolean | null;
}

export function validarDatosGenerales(d: DatosGenerales): string[] {
  const e: string[] = [];
  if (d.site_area_m2 !== null && !(d.site_area_m2 > 0)) e.push('El área total debe ser mayor que 0 m².');
  if (d.site_workers !== null && (!Number.isInteger(d.site_workers) || d.site_workers < 0)) e.push('El número de trabajadores debe ser un entero de 0 o más.');
  if (d.site_occupancy !== null && !OCUPACIONES.includes(d.site_occupancy)) e.push('Ocupación no válida.');
  if (d.canton_code !== null && !/^[0-9]{4}$/.test(d.canton_code)) e.push('Código de cantón no válido.');
  if (d.seismic_zone !== null && !ZONAS_SISMICAS.includes(d.seismic_zone)) e.push('Zona sísmica no válida.');
  if (d.seismic_zone !== null && (d.seismic_zone_source === null || !FUENTES_ZONA.includes(d.seismic_zone_source))) {
    e.push('La zona sísmica necesita su fuente (Tabla 19 de la NEC o manual).');
  }
  if (d.seismic_zone !== null && d.seismic_zone_source === 'nec_tabla19' && !d.seismic_place?.trim()) {
    e.push('Elige la población de la Tabla 19 de la NEC.');
  }
  if (d.fire_station_km !== null && !(d.fire_station_km >= 0 && d.fire_station_km < 10000)) e.push('La distancia a bomberos debe estar entre 0 y 9.999,99 km.');
  if (d.fire_station_min !== null && !(d.fire_station_min >= 0 && d.fire_station_min < 100000)) e.push('El tiempo de llegada de bomberos debe ser 0 min o más.');
  return e;
}

// Un plan por empresa (company_id único, igual que EmergencyPlanPage). current_version no se escribe aquí:
// la sube la aprobación de una versión (PE-25).
export async function guardarDatosGenerales(companyId: string, datos: DatosGenerales, usuarioId: string | null, existente: FilaPlan | null): Promise<FilaPlan> {
  const errores = validarDatosGenerales(datos);
  if (errores.length) throw new Error(errores.join(' '));
  const payload = {
    code: datos.code?.trim() || null,
    canton_code: datos.canton_code,
    site_canton: datos.site_canton?.trim() || null,
    site_province: datos.site_province?.trim() || null,
    site_area_m2: datos.site_area_m2,
    site_occupancy: datos.site_occupancy,
    site_workers: datos.site_workers,
    seismic_zone: datos.seismic_zone,
    seismic_zone_source: datos.seismic_zone ? datos.seismic_zone_source : null,
    // La población solo se guarda con la zona de la NEC; con zona manual queda vacía.
    seismic_place: datos.seismic_zone && datos.seismic_zone_source === 'nec_tabla19' ? datos.seismic_place?.trim() || null : null,
    fire_station_km: datos.fire_station_km,
    fire_station_min: datos.fire_station_min,
    site_is_new: datos.site_is_new ?? null,
    updated_at: new Date().toISOString(),
  };
  if (existente) {
    const { data, error } = await supabase.from('emergency_plans').update(payload).eq('id', existente.id).select('*');
    if (error) falla(error, 'guardar los datos generales');
    return mapPlan(exigeFilas(data, 'guardar los datos generales')[0]);
  }
  const { data, error } = await supabase.from('emergency_plans')
    .insert({ ...payload, company_id: companyId, status: 'borrador', created_by: usuarioId })
    .select('*');
  if (error) falla(error, 'crear el plan');
  return mapPlan(exigeFilas(data, 'crear el plan')[0]);
}

export async function contarTrabajadoresActivos(companyId: string): Promise<number> {
  const { count, error } = await supabase.from('workers').select('id', { count: 'exact', head: true })
    .eq('company_id', companyId).eq('status', 'activo');
  if (error) falla(error, 'contar los trabajadores activos');
  return count ?? 0;
}

// ── Sectores y filas de carga de fuego (paso 2) ─────────────────────────────

export async function cargarSectores(planId: string): Promise<FilaSector[]> {
  const { data, error } = await supabase.from('emergency_plan_sectors').select('*').eq('plan_id', planId)
    .order('sort_order').order('created_at');
  if (error) falla(error, 'leer los sectores');
  return (data ?? []).map(mapSector);
}

export async function cargarFilasCarga(sectorIds: string[]): Promise<FilaCargaFuego[]> {
  if (sectorIds.length === 0) return [];
  const { data, error } = await supabase.from('emergency_fire_load_items').select('*').in('sector_id', sectorIds)
    .order('sort_order').order('created_at');
  if (error) falla(error, 'leer las filas de carga de fuego');
  return (data ?? []).map(mapFilaCarga);
}

export type SectorEditable = Pick<FilaSector, 'name' | 'occupancy' | 'area_m2' | 'fire_separated' | 'group_key' | 'sort_order'
  | 'r_value' | 'r_origin' | 'r_source'> & {
    id?: string;
    occupants?: number | null;   // RTQ 5, 5.2.2 (PE-A3c2)
    has_sprinklers?: boolean;    // rociadores automáticos: recorrido de la RTQ 3 (PE-A3c, M33; NOT NULL, por defecto false)
  };
export type FilaCargaEditable = Omit<FilaCargaFuego, 'id' | 'company_id' | 'sector_id' | 'created_at'> & { id?: string };

export function validarSector(s: SectorEditable, filas: FilaCargaEditable[]): string[] {
  const e: string[] = [];
  if (!s.name.trim()) e.push('El sector necesita un nombre.');
  if (!(s.area_m2 > 0)) e.push('El área del sector debe ser mayor que 0 m².');
  if (!OCUPACIONES.includes(s.occupancy)) e.push('Actividad no válida.');
  if (s.occupants !== undefined && s.occupants !== null && !(Number.isInteger(s.occupants) && s.occupants >= 0)) e.push('Los ocupantes del sector deben ser un número entero de 0 o más.');
  if (s.r_origin === 'tabla' && !R_TABLA.includes(s.r_value as number)) e.push('R de tabla debe ser 0,8; 1; 1,4 o 1,8.');
  if (s.r_origin === 'manual' && (!(Number(s.r_value) > 0) || !s.r_source?.trim())) e.push('R manual necesita un valor mayor que 0 y su fuente.');
  if (s.r_origin === null && (s.r_value !== null || s.r_source)) e.push('R incompleto.');
  filas.forEach((f, i) => {
    const n = `Fila ${i + 1}${f.material ? ` (${f.material})` : ''}`;
    if (!f.material.trim()) e.push(`Fila ${i + 1}: falta el material.`);
    if (!(f.q_value >= 0)) e.push(`${n}: qi inválido.`);
    if (f.q_source === 'manual' && !f.q_source_note?.trim()) e.push(`${n}: qi manual necesita su fuente (hoja de seguridad, ficha técnica…).`);
    if (!(f.ci > 0)) e.push(`${n}: Ci inválido.`);
    if (f.ci_origin === 'manual' && !f.ci_source?.trim()) e.push(`${n}: Ci manual necesita su fuente.`);
    const mat = f.q_source !== 'manual' ? materialPorClave(f.table_ref) : undefined;
    if (mat && f.ci_origin === 'tabla' && f.ci_grade === null && f.ci !== mat.ci) {
      if (!(permiteCiCompacto(mat) && f.ci === CI_NOTA_COMPACTO)) e.push(`${n}: el Ci no coincide con la fila de la tabla.`);
      else if (!f.ci_source?.trim()) e.push(`${n}: Ci = 1 por la nota ${mat.ciNota} necesita la justificación.`);
    }
    if (f.mass_kg === null || !(f.mass_kg >= 0)) e.push(`${n}: falta la masa en kg.`);
  });
  return e;
}

// Guarda el sector y sus filas. No borra filas: borrar es solo del administrador (borrarFilaCarga).
export async function guardarSector(
  companyId: string, planId: string, sector: SectorEditable, filas: FilaCargaEditable[], usuarioId: string | null,
): Promise<{ sector: FilaSector; filas: FilaCargaFuego[] }> {
  const errores = validarSector(sector, filas);
  if (errores.length) throw new Error(errores.join(' '));
  const datos = {
    name: sector.name.trim(), occupancy: sector.occupancy, area_m2: sector.area_m2, fire_separated: sector.fire_separated,
    group_key: sector.fire_separated ? null : (sector.group_key?.trim() || null), sort_order: sector.sort_order,
    r_value: sector.r_origin ? sector.r_value : null, r_origin: sector.r_origin,
    r_source: sector.r_origin === 'manual' ? sector.r_source?.trim() || null : null,
    // Solo si la pantalla lo manda: así no se borra lo guardado desde otra pantalla.
    ...(sector.occupants !== undefined ? { occupants: sector.occupants } : {}),
    ...(sector.has_sprinklers !== undefined ? { has_sprinklers: sector.has_sprinklers } : {}),
  };
  let guardado: FilaSector;
  if (sector.id) {
    const { data, error } = await supabase.from('emergency_plan_sectors')
      .update({ ...datos, updated_at: new Date().toISOString() }).eq('id', sector.id).select('*');
    if (error) falla(error, 'guardar el sector');
    guardado = mapSector(exigeFilas(data, 'guardar el sector')[0]);
  } else {
    const { data, error } = await supabase.from('emergency_plan_sectors')
      .insert({ ...datos, company_id: companyId, plan_id: planId, created_by: usuarioId }).select('*');
    if (error) falla(error, 'crear el sector');
    guardado = mapSector(exigeFilas(data, 'crear el sector')[0]);
  }

  const out: FilaCargaFuego[] = [];
  for (let i = 0; i < filas.length; i++) {
    const { id, origin_note, ...resto } = filas[i];
    const fila = {
      ...resto, sort_order: i, material: resto.material.trim(),
      // M37: la traza de origen solo se manda si existe (así un sector sin filas del inventario se guarda aunque falte M37).
      ...(origin_note?.trim() ? { origin_note: origin_note.trim().slice(0, 300) } : {}),
      q_source_note: resto.q_source === 'manual' ? resto.q_source_note?.trim() || null : null,
      // Ci manual: su fuente. Ci = 1 por las notas 5 o 6: su justificación (la base lo permite con 'tabla').
      ci_source: resto.ci_source?.trim() || null,
    };
    if (id) {
      const { data, error } = await supabase.from('emergency_fire_load_items').update(fila).eq('id', id).select('*');
      if (error) falla(error, `guardar la fila "${fila.material}"`);
      out.push(mapFilaCarga(exigeFilas(data, `guardar la fila "${fila.material}"`)[0]));
    } else {
      const { data, error } = await supabase.from('emergency_fire_load_items')
        .insert({ ...fila, company_id: companyId, sector_id: guardado.id }).select('*');
      if (error) falla(error, `guardar la fila "${fila.material}"`);
      out.push(mapFilaCarga(exigeFilas(data, `guardar la fila "${fila.material}"`)[0]));
    }
  }
  return { sector: guardado, filas: out };
}

// Borrar el sector borra en cascada sus filas y sus evaluaciones (FK ON DELETE CASCADE de M33).
export async function borrarSector(sectorId: string): Promise<void> {
  const { data, error } = await supabase.from('emergency_plan_sectors').delete().eq('id', sectorId).select('id');
  if (error) falla(error, 'borrar el sector');
  exigeFilas(data, 'borrar el sector');
}

export const MENSAJE_PLAN_CON_VERSIONES = 'El plan tiene versiones aprobadas; no se puede borrar.';

// Borrar el plan (PE-A3c; solo el administrador: política emergency_plans_delete de M32). Sectores, filas,
// evaluaciones y acciones se borran en cascada (FK de M33); los simulacros quedan con plan_id en NULL. Las
// versiones (emergency_plan_versions) tienen FK NO ACTION: con alguna, el plan no se borra; se revisa antes y,
// si aun así la FK lo frena (23503), se da el mismo aviso.
export async function contarVersionesPlan(planId: string): Promise<number> {
  const { count, error } = await supabase.from('emergency_plan_versions')
    .select('id', { count: 'exact', head: true }).eq('plan_id', planId);
  if (error) falla(error, 'revisar las versiones del plan');
  return count ?? 0;
}

export async function borrarPlan(plan: Pick<FilaPlan, 'id' | 'company_id'>): Promise<void> {
  if (await contarVersionesPlan(plan.id) > 0) throw new Error(MENSAJE_PLAN_CON_VERSIONES);
  const { data, error } = await supabase.from('emergency_plans').delete().eq('id', plan.id).eq('company_id', plan.company_id).select('id');
  if (error?.code === '23503') throw new Error(MENSAJE_PLAN_CON_VERSIONES);
  if (error) falla(error, 'borrar el plan');
  exigeFilas(data, 'borrar el plan');
}

export async function borrarFilaCarga(filaId: string): Promise<void> {
  const { data, error } = await supabase.from('emergency_fire_load_items').delete().eq('id', filaId).select('id');
  if (error) falla(error, 'borrar la fila de carga de fuego');
  exigeFilas(data, 'borrar la fila de carga de fuego');
}

// ── Evaluaciones (emergency_assessments) ─────────────────────────────────────

export async function cargarEvaluaciones(planId: string): Promise<FilaEvaluacion[]> {
  const { data, error } = await supabase.from('emergency_assessments').select('*').eq('plan_id', planId);
  if (error) falla(error, 'leer las evaluaciones');
  return (data ?? []) as FilaEvaluacion[];
}

export interface EvaluacionAGuardar {
  companyId: string;
  planId: string;
  sectorId: string | null;
  method: MetodoEvaluacion;
  inputs: Record<string, unknown>;
  result?: Record<string, unknown>;
  traces?: Traza[];
  usuarioId: string | null;
}

// El índice único de M33 es (plan_id, coalesce(sector_id, 0…0), method): PostgREST no puede usarlo como
// onConflict, así que se busca la fila y se actualiza o se inserta.
export async function guardarEvaluacion(ev: EvaluacionAGuardar): Promise<FilaEvaluacion> {
  let q = supabase.from('emergency_assessments').select('id, inputs').eq('plan_id', ev.planId).eq('method', ev.method);
  q = ev.sectorId ? q.eq('sector_id', ev.sectorId) : q.is('sector_id', null);
  const { data: previas, error: e1 } = await q;
  if (e1) falla(e1, 'leer la evaluación');
  const fila = {
    inputs: ev.inputs, result: ev.result ?? {}, traces: ev.traces ?? [], engine_version: PE_MOTOR_VERSION,
    tables_version: PE_TABLAS_VERSION, computed_at: new Date().toISOString(), computed_by: ev.usuarioId,
  };
  const previa = (previas ?? [])[0];
  if (previa) {
    const { data, error } = await supabase.from('emergency_assessments').update(fila).eq('id', previa.id).select('*');
    if (error) falla(error, 'guardar la evaluación');
    return exigeFilas(data, 'guardar la evaluación')[0] as FilaEvaluacion;
  }
  const { data, error } = await supabase.from('emergency_assessments')
    .insert({ ...fila, company_id: ev.companyId, plan_id: ev.planId, sector_id: ev.sectorId, method: ev.method }).select('*');
  if (error) falla(error, 'guardar la evaluación');
  return exigeFilas(data, 'guardar la evaluación')[0] as FilaEvaluacion;
}

export function buscarEvaluacion(evs: FilaEvaluacion[], method: MetodoEvaluacion, sectorId: string | null): FilaEvaluacion | undefined {
  return evs.find(e => e.method === method && (e.sector_id ?? null) === sectorId);
}

// ── R del sector (columnas de M34) ───────────────────────────────────────────

export interface RSector { r: number; rOrigen: OrigenCoeficiente; rFuente: string | null }

// Sin R guardado: R = 1 por defecto (RD 164, Tabla 1.3.4).
export const R_POR_DEFECTO: RSector = { r: 1, rOrigen: 'tabla', rFuente: null };

export function rDeFila(s: Pick<FilaSector, 'r_value' | 'r_origin' | 'r_source'>): RSector {
  if (!s.r_origin || !(Number(s.r_value) > 0)) return R_POR_DEFECTO;
  return { r: Number(s.r_value), rOrigen: s.r_origin, rFuente: s.r_source ?? null };
}

// ── PE-A3b: brigada, trabajadores, acciones y borrados ───────────────────────

// Personas distintas en la brigada (emergency_brigade_members). Si no se puede leer, null: la verificación
// queda 'por_validar' en vez de suponer que no hay brigada.
export async function contarBrigada(companyId: string): Promise<number | null> {
  const { data, error } = await supabase.from('emergency_brigade_members').select('worker_id').eq('company_id', companyId);
  if (error) { console.error('contarBrigada', error); return null; }
  return new Set((data ?? []).map((f: any) => f.worker_id)).size;
}

// PE-38: capacitación de cada brigadista (una fila por rol). null si no se pudo leer.
export async function cargarBrigadaCapacitacion(companyId: string): Promise<{ workerId: string; trained: boolean; trainingDate: string | null }[] | null> {
  const { data, error } = await supabase.from('emergency_brigade_members').select('worker_id, trained, training_date').eq('company_id', companyId);
  if (error) { console.error('cargarBrigadaCapacitacion', error); return null; }
  return (data ?? []).map((f: any) => ({ workerId: f.worker_id, trained: !!f.trained, trainingDate: f.training_date ?? null }));
}

export async function cargarTrabajadores(companyId: string): Promise<{ id: string; full_name: string }[]> {
  const { data, error } = await supabase.from('workers').select('id, full_name').eq('company_id', companyId)
    .eq('status', 'activo').order('full_name');
  if (error) falla(error, 'leer los trabajadores');
  return (data ?? []) as { id: string; full_name: string }[];
}

export async function cargarAcciones(planId: string): Promise<FilaAccion[]> {
  const { data, error } = await supabase.from('emergency_plan_actions').select('*').eq('plan_id', planId)
    .order('is_mandatory', { ascending: false }).order('created_at');
  if (error) falla(error, 'leer el plan de acción');
  return (data ?? []) as FilaAccion[];
}

// Inserta o actualiza una acción. Las obligatorias exigen responsable y fecha (validarAccion; M35 lo exige
// también con un CHECK). La clave de origen es única por plan (M35): si ya existe, no se duplica.
export async function guardarAccion(companyId: string, planId: string, a: AccionEditable, usuarioId: string | null): Promise<FilaAccion> {
  const errores = validarAccion(a);
  if (errores.length) throw new Error(errores.join(' '));
  const datos = {
    description: a.descripcion.trim(), responsible_worker_id: a.responsableId, responsible_role: a.responsableCargo.trim() || null,
    due_date: a.fecha || null, status: a.estado, updated_at: new Date().toISOString(),
    closed_at: a.estado === 'cerrada' ? new Date().toISOString() : null, closed_by: a.estado === 'cerrada' ? usuarioId : null,
  };
  if (a.id) {
    const { data, error } = await supabase.from('emergency_plan_actions').update(datos).eq('id', a.id).select('*');
    if (error) falla(error, 'guardar la acción');
    return exigeFilas(data, 'guardar la acción')[0] as FilaAccion;
  }
  if (a.sourceKey) {
    const { data: previa, error: e1 } = await supabase.from('emergency_plan_actions').select('id').eq('plan_id', planId).eq('source_key', a.sourceKey);
    if (e1) falla(e1, 'leer el plan de acción');
    if ((previa ?? []).length) throw new Error('Esta acción ya está guardada: no se duplica.');
  }
  const { data, error } = await supabase.from('emergency_plan_actions').insert({
    ...datos, company_id: companyId, plan_id: planId, sector_id: a.sectorId, source_method: a.sourceMethod, norma: a.norma,
    articulo: a.articulo, source_key: a.sourceKey, is_mandatory: a.obligatoria, annex_item: ITEM_PO18, created_by: usuarioId,
  }).select('*');
  if (error) falla(error, 'guardar la acción');
  return exigeFilas(data, 'guardar la acción')[0] as FilaAccion;
}

// "Origen resuelto": la acción no se borra; solo se marca (o se desmarca si el origen vuelve a fallar).
export async function marcarOrigenResuelto(ids: string[], resuelto: boolean): Promise<void> {
  if (ids.length === 0) return;
  const { data, error } = await supabase.from('emergency_plan_actions')
    .update({ source_resolved_at: resuelto ? new Date().toISOString() : null, updated_at: new Date().toISOString() }).in('id', ids).select('id');
  if (error) falla(error, 'actualizar el plan de acción');
  exigeFilas(data, 'actualizar el plan de acción');
}

export async function borrarAccion(id: string): Promise<void> {
  const { data, error } = await supabase.from('emergency_plan_actions').delete().eq('id', id).select('id');
  if (error) falla(error, 'borrar la acción');
  exigeFilas(data, 'borrar la acción');
}

export async function borrarEvaluacion(id: string): Promise<void> {
  const { data, error } = await supabase.from('emergency_assessments').delete().eq('id', id).select('id');
  if (error) falla(error, 'borrar la evaluación');
  exigeFilas(data, 'borrar la evaluación');
}

// Borra filas meseri de grupos anteriores: solo method = 'meseri', solo ese plan y solo esos ids (admin, RLS).
export async function borrarEvaluacionesMeseri(planId: string, ids: string[]): Promise<number> {
  if (ids.length === 0) return 0;
  const { data, error } = await supabase.from('emergency_assessments').delete()
    .eq('plan_id', planId).eq('method', 'meseri').in('id', ids).select('id');
  if (error) falla(error, 'limpiar las evaluaciones MESERI de grupos anteriores');
  return (data ?? []).length;
}

// ── Guardado (M37): aparte de los datos generales, para que el paso 1 siga funcionando sin M37 ──

export async function guardarContinuidadYTransferencia(plan: Pick<FilaPlan, 'id'>, c: Continuidad, t: Transferencia): Promise<void> {
  const errores = [...validarContinuidad(c), ...validarTransferencia(t)];
  if (errores.length) throw new Error(errores.join(' '));
  const l = limpiarContinuidad(c);
  const si = t.tiene === true;
  const { data, error } = await supabase.from('emergency_plans').update({
    business_continuity: continuidadVaciaDel(l) ? null : l,
    risk_transfer: t.tiene,
    risk_transfer_type: si ? t.tipo.trim() || null : null,
    risk_transfer_insurer: si ? t.aseguradora.trim() || null : null,
    risk_transfer_valid_until: si ? t.vigenteHasta || null : null,
    updated_at: new Date().toISOString(),
  }).eq('id', plan.id).select('id');
  if (error) {
    if (error.code === '42703' || error.code === 'PGRST204') throw new Error('No se pudo guardar la continuidad: falta actualizar la base de datos. Avisa al soporte de StorSafe.' /* M37 */);
    throw new Error(mensajeErrorPE(error, 'guardar la continuidad y la póliza'));
  }
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
}
